import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useCreateApplicationForShopper } from '@/hooks/peo/useCreateApplicationForShopper';

interface CreateApplicationDialogProps {
  peoProviderId: string;
  onApplicationCreated?: () => void;
  buttonClassName?: string;
  buttonVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "glass" | "glass-primary";
}

export const CreateApplicationDialog = ({ 
  peoProviderId, 
  onApplicationCreated,
  buttonClassName = "w-full",
  buttonVariant = "default"
}: CreateApplicationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [shopperEmail, setShopperEmail] = useState('');
  const { createApplicationForShopper, isCreating } = useCreateApplicationForShopper();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!shopperEmail.trim()) {
      return;
    }

    try {
      await createApplicationForShopper(shopperEmail.trim(), peoProviderId);
      setOpen(false);
      setShopperEmail('');
      onApplicationCreated?.();
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={buttonClassName} variant={buttonVariant}>
          <Plus className="h-6 w-6" />
          <span>Create Application</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Application for Shopper</DialogTitle>
          <DialogDescription>
            Create a new PEO application on behalf of a shopper. They will be notified via email and can access the application when they create an account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="shopperEmail">Shopper Email Address</Label>
            <Input
              id="shopperEmail"
              type="email"
              placeholder="shopper@example.com"
              value={shopperEmail}
              onChange={(e) => setShopperEmail(e.target.value)}
              required
            />
            <p className="text-sm text-muted-foreground">
              Enter the email address of the shopper you want to create an application for.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Application'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};